import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Play, Pause, Stop, Record, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import { getTour } from './tourDefinitions';
import { resolveTarget } from './tourTargets';
import { startTabRecording } from './tourRecorder';
import TourRecordingDialog from './TourRecordingDialog';

const START_EVENT = 'help-tour:start';
const WAIT_MS = 3000;
const CARD_WIDTH = 340;
const PAD = 6;
const CAN_SPEAK = typeof window !== 'undefined' && 'speechSynthesis' in window;

/**
 * Start a tour from anywhere (e.g. the Help page). Call it from a click
 * handler — recording needs the click to ask for screen-share permission.
 *   mode: 'manual' (Next / Back)  | 'video' (plays by itself, narrated)
 *       | 'record' (plays as video and records the tab; admins)
 *   topic: the help topic a recording is saved to.
 */
export function startHelpTour(tourId, { mode = 'manual', topic = null } = {}) {
  window.dispatchEvent(new CustomEvent(START_EVENT, { detail: { tourId, mode, topic } }));
}

function waitFor(fn, timeout, isCancelled) {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (isCancelled()) return resolve(null);
      const found = fn();
      if (found || Date.now() - started > timeout) return resolve(found || null);
      setTimeout(tick, 150);
    };
    tick();
  });
}

function sameRect(a, b) {
  return a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function readingTime(step) {
  return Math.min(11000, 2500 + `${step.title} ${step.body}`.length * 50);
}

function Pointer({ rect }) {
  if (!rect) return null;
  const x = rect.left + Math.min(rect.width * 0.5, 120);
  const y = rect.top + Math.min(rect.height * 0.5, 22);
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      style={{
        position: 'fixed', left: x, top: y, transition: 'left 0.8s ease-in-out, top 0.8s ease-in-out',
        filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.45))', zIndex: 2,
      }}
      aria-hidden="true"
    >
      <path d="M4 2l16 9-7 2-3 7z" fill="white" stroke="#111827" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Mounted once in the dashboard layout. Highlights one element at a time with
 * a dimmed backdrop and an explanation card. In video mode it plays by itself
 * with narration and a moving pointer, like a tutorial video on the real page.
 */
function HelpTourRunner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tour, setTour] = useState(null);
  const [mode, setMode] = useState('manual');
  const [topic, setTopic] = useState(null);
  const [steps, setSteps] = useState([]);
  const [index, setIndex] = useState(0);
  const [element, setElement] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [rect, setRect] = useState(null);
  const [cardSize, setCardSize] = useState({ height: 180 });
  const [playing, setPlaying] = useState(false);
  const [voice, setVoice] = useState(CAN_SPEAK);
  const [stepStarted, setStepStarted] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordError, setRecordError] = useState('');
  const direction = useRef(1);
  const runId = useRef(0);
  const cardRef = useRef(null);
  const recorderRef = useRef(null);
  const advanceTimer = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const autoplay = mode !== 'manual';

  const stopNarration = useCallback(() => {
    clearTimeout(advanceTimer.current);
    if (CAN_SPEAK) window.speechSynthesis.cancel();
  }, []);

  const end = useCallback(async () => {
    runId.current += 1;
    stopNarration();
    setTour(null);
    setSteps([]);
    setElement(null);
    setRect(null);
    setPlaying(false);
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder) setRecordedBlob(await recorder.stop());
  }, [stopNarration]);

  const begin = useCallback((next, nextMode, nextTopic) => {
    runId.current += 1;
    direction.current = 1;
    setTour(next);
    setMode(nextMode);
    setTopic(nextTopic);
    setSteps(next.steps);
    setIndex(0);
    setElement(null);
    setPlaying(nextMode !== 'manual');
    if (pathRef.current !== next.route) navigate(next.route);
  }, [navigate]);

  useEffect(() => {
    const onStart = (e) => {
      const next = getTour(e.detail?.tourId);
      if (!next) return;
      const nextMode = e.detail?.mode || 'manual';
      const nextTopic = e.detail?.topic || null;
      setRecordError('');
      if (nextMode !== 'record') {
        begin(next, nextMode, nextTopic);
        return;
      }
      // Still inside the click — the browser asks which tab to share.
      startTabRecording()
        .then((recorder) => {
          recorderRef.current = recorder;
          recorder.onEnded(() => end());
          // Short pause so the share prompt is gone before the first frame.
          setTimeout(() => begin(next, 'record', nextTopic), 600);
        })
        .catch(() => setRecordError('Recording was cancelled or is not allowed in this browser.'));
    };
    window.addEventListener(START_EVENT, onStart);
    return () => window.removeEventListener(START_EVENT, onStart);
  }, [begin, end]);

  // Leaving the tour's page (e.g. via the sidebar) ends the tour.
  useEffect(() => {
    if (tour && location.pathname !== tour.route) {
      const t = setTimeout(() => {
        if (pathRef.current.replace(/\/+$/, '') !== tour.route) end();
      }, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.pathname, tour, end]);

  // Resolve the current step: expand generated steps, find its element, or skip it.
  useEffect(() => {
    if (!tour || !steps.length) return undefined;
    if (index < 0 || index >= steps.length) {
      if (index >= steps.length) end();
      else {
        direction.current = 1;
        setIndex(0);
      }
      return undefined;
    }
    const run = ++runId.current;
    const cancelled = () => run !== runId.current;
    const step = steps[index];
    setResolving(true);

    (async () => {
      if (step.expand) {
        // Give the page a moment to render the fields being expanded.
        const generated = await waitFor(() => {
          const s = step.expand();
          return s.length ? s : null;
        }, 1500, cancelled);
        if (cancelled()) return;
        const replacement = generated || (step.fallback ? [step.fallback] : []);
        setSteps((prev) => [...prev.slice(0, index), ...replacement, ...prev.slice(index + 1)]);
        if (!replacement.length && direction.current < 0) setIndex((i) => i - 1);
        return;
      }
      if (!step.target) {
        setElement(null);
        setResolving(false);
        setStepStarted(Date.now());
        return;
      }
      const found = await waitFor(() => resolveTarget(step.target), WAIT_MS, cancelled);
      if (cancelled()) return;
      if (!found) {
        // Not on this page for this user/product — skip it.
        setIndex((i) => i + direction.current);
        return;
      }
      found.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setElement(found);
      setResolving(false);
      setStepStarted(Date.now());
    })();

    return () => { runId.current += 1; };
  }, [tour, steps, index, end]);

  // Keep the spotlight on the element while the page scrolls or re-lays out.
  useEffect(() => {
    if (!element) {
      setRect(null);
      return undefined;
    }
    let frame;
    let last = null;
    const loop = () => {
      if (element.isConnected) {
        const r = element.getBoundingClientRect();
        const next = { top: r.top, left: r.left, width: r.width, height: r.height };
        if (!sameRect(next, last)) {
          last = next;
          setRect(next);
        }
      }
      frame = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(frame);
  }, [element]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardSize({ height: cardRef.current.offsetHeight });
  }, [index, steps, rect, mode]);

  const go = useCallback((delta) => {
    stopNarration();
    direction.current = delta;
    setResolving(true);
    setElement(null);
    setIndex((i) => i + delta);
  }, [stopNarration]);

  // Video mode: narrate the step, then move on by itself.
  useEffect(() => {
    if (!tour || !autoplay || !playing || resolving || !stepStarted) return undefined;
    const step = steps[index];
    if (!step || step.expand) return undefined;
    const advance = () => {
      advanceTimer.current = setTimeout(() => go(1), 900);
    };
    // Let the scroll and pointer settle before speaking.
    const startDelay = setTimeout(() => {
      if (voice && CAN_SPEAK) {
        const u = new SpeechSynthesisUtterance(`${step.title}. ${step.body}`);
        u.rate = 1;
        u.onend = advance;
        u.onerror = () => { advanceTimer.current = setTimeout(() => go(1), readingTime(step)); };
        window.speechSynthesis.speak(u);
      } else {
        advanceTimer.current = setTimeout(() => go(1), readingTime(step));
      }
    }, 900);
    return () => {
      clearTimeout(startDelay);
      stopNarration();
    };
  }, [tour, autoplay, playing, resolving, stepStarted, steps, index, voice, go, stopNarration]);

  useEffect(() => {
    if (!tour) return undefined;
    const onKey = (e) => {
      // Arrow keys belong to the field while someone is typing in it.
      const typing = e.target.closest?.('input, textarea, select, [contenteditable="true"], .tk-ss');
      if (e.key === 'Escape') end();
      else if (typing) return;
      else if (e.key === 'ArrowRight' && !resolving) go(1);
      else if (e.key === 'ArrowLeft' && !resolving && index > 0) go(-1);
      else if (e.key === ' ' && autoplay) {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tour, end, go, resolving, index, autoplay]);

  useEffect(() => () => stopNarration(), [stopNarration]);

  const dialogs = (
    <>
      {recordedBlob && (
        <TourRecordingDialog blob={recordedBlob} topic={topic} onClose={() => setRecordedBlob(null)} />
      )}
      {recordError && (
        <div
          role="alert"
          style={{
            position: 'fixed', bottom: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 3100,
            background: '#111827', color: 'white', padding: '10px 14px', borderRadius: '6px', fontSize: '12px',
            display: 'flex', gap: '10px', alignItems: 'center',
          }}
        >
          {recordError}
          <button type="button" onClick={() => setRecordError('')} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
      )}
    </>
  );

  if (!tour || index < 0 || index >= steps.length) return createPortal(dialogs, document.body);
  const step = steps[index];
  if (step.expand || (resolving && step.target)) return createPortal(dialogs, document.body);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(CARD_WIDTH, vw - 24);
  let cardStyle;
  if (rect) {
    const below = rect.top + rect.height + PAD + 12;
    const fitsBelow = below + cardSize.height < vh - 8;
    const top = fitsBelow ? below : Math.max(8, rect.top - PAD - 12 - cardSize.height);
    const left = Math.min(Math.max(12, rect.left), vw - width - 12);
    cardStyle = { top, left };
  } else {
    cardStyle = { top: Math.max(12, (vh - cardSize.height) / 2), left: (vw - width) / 2 };
  }
  const isLast = index === steps.length - 1;
  const iconBtn = {
    background: 'transparent', border: '1px solid #D1D5DB', borderRadius: '4px', cursor: 'pointer',
    padding: '4px 6px', display: 'flex', alignItems: 'center', color: '#111827',
  };

  return createPortal(
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 3000, pointerEvents: autoplay ? 'auto' : 'none' }} data-testid="help-tour">
        {rect ? (
          <div
            style={{
              position: 'fixed',
              top: rect.top - PAD,
              left: rect.left - PAD,
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2,
              borderRadius: '6px',
              border: '2px solid #3B82F6',
              boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
              transition: 'top 0.5s, left 0.5s, width 0.5s, height 0.5s',
            }}
          />
        ) : (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)' }} />
        )}

        {autoplay && <Pointer rect={rect} />}

        {mode === 'record' && (
          <div
            style={{
              position: 'fixed', top: '10px', right: '12px', display: 'flex', alignItems: 'center', gap: '6px',
              background: 'rgba(220, 38, 38, 0.92)', color: 'white', fontSize: '11px', fontWeight: 600,
              padding: '4px 10px', borderRadius: '999px',
            }}
          >
            <Record size={12} weight="fill" /> Recording
          </div>
        )}

        <div
          ref={cardRef}
          role="dialog"
          aria-label={step.title}
          style={{
            position: 'fixed', ...cardStyle, width, pointerEvents: 'auto',
            background: 'white', borderRadius: '8px', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.35)',
            padding: '14px 16px', transition: 'top 0.5s, left 0.5s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
            <p style={{ fontSize: '11px', color: '#2563EB', fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {tour.title}
            </p>
            <button
              type="button"
              onClick={end}
              aria-label={mode === 'record' ? 'Stop recording' : 'Close tour'}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#6B7280' }}
            >
              <X size={16} />
            </button>
          </div>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#111827', margin: '6px 0' }}>{step.title}</h3>
          <p style={{ fontSize: '12.5px', color: '#374151', lineHeight: 1.6, margin: 0 }}>{step.body}</p>

          {autoplay && (
            <div style={{ display: 'flex', gap: '3px', marginTop: '12px' }} aria-hidden="true">
              {steps.map((s, i) => (
                <div
                  key={i}
                  style={{ flex: 1, height: '3px', borderRadius: '2px', background: i < index ? '#93C5FD' : i === index ? '#2563EB' : '#E5E7EB' }}
                />
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
            <span style={{ fontSize: '11px', color: '#6B7280', flex: 1 }}>{index + 1} of {steps.length}</span>
            {autoplay ? (
              <>
                {CAN_SPEAK && (
                  <button type="button" style={iconBtn} onClick={() => setVoice((v) => !v)} aria-label={voice ? 'Mute narration' : 'Turn on narration'}>
                    {voice ? <SpeakerHigh size={14} /> : <SpeakerSlash size={14} />}
                  </button>
                )}
                <button type="button" style={iconBtn} onClick={() => setPlaying((p) => !p)} aria-label={playing ? 'Pause' : 'Play'}>
                  {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                </button>
                <button type="button" style={iconBtn} onClick={end} aria-label="Stop">
                  <Stop size={14} weight="fill" />
                </button>
              </>
            ) : (
              <>
                {index > 0 && (
                  <button type="button" className="btn-secondary" style={{ fontSize: '12px' }} onClick={() => go(-1)}>
                    Back
                  </button>
                )}
                <button type="button" className="btn-primary" style={{ fontSize: '12px' }} onClick={() => (isLast ? end() : go(1))}>
                  {isLast ? 'Finish' : 'Next'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {dialogs}
    </>,
    document.body,
  );
}

export default HelpTourRunner;
